import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

/**
 * Markdown written by agents (finding bodies, recommendations) rendered as
 * prose. Headings are demoted so a body's `#` never competes with the
 * page's single `<h1>`; links open in a new tab.
 */
export function MarkdownProse({
  content,
  className,
  lineBreaks = false,
}: {
  content: string;
  className?: string;
  /**
   * Keep each line on its own line, as the CLI's own reports do. A
   * recommendation ends in "**Quick fix:** …", "**Shared cause:** …" and
   * "**Part of:** …" lines that would otherwise run into one paragraph.
   */
  lineBreaks?: boolean;
}) {
  return (
    <div
      className={cn(
        // Typography's own greys ignore the theme tokens (and failed AAA
        // on tonal surfaces); every prose colour follows the container.
        // Typography's own sizes (16/28 body, em-scaled headings) sit off
        // the type scale; pin body and every heading level to it. Sans
        // throughout — rendered markdown is analysis, not a persona voice.
        "prose max-w-none font-sans text-base leading-relaxed prose-p:my-3 prose-headings:mt-5 prose-headings:mb-2 prose-headings:font-sans prose-headings:font-semibold prose-headings:text-base prose-h1:text-md prose-h2:text-md prose-h3:text-base prose-h4:text-base",
        "[--tw-prose-body:currentColor] [--tw-prose-headings:currentColor] [--tw-prose-bold:currentColor] [--tw-prose-links:currentColor] [--tw-prose-bullets:currentColor] [--tw-prose-counters:currentColor] [--tw-prose-quotes:currentColor] [--tw-prose-code:currentColor]",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h3>{children}</h3>,
          h2: ({ children }) => <h3>{children}</h3>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {lineBreaks ? withLineBreaks(content) : content}
      </ReactMarkdown>
    </div>
  );
}

/** Every single line break outside a code fence as a hard break. */
export function withLineBreaks(markdown: string): string {
  const fence = (line: string | undefined) => /^\s*(```|~~~)/.test(line ?? "");
  let fenced = false;
  return markdown
    .split("\n")
    .map((line, i, all) => {
      if (fence(line)) {
        fenced = !fenced;
        return line;
      }
      const next = all[i + 1];
      return !fenced && line.trim() && next?.trim() && !fence(next)
        ? `${line}  `
        : line;
    })
    .join("\n");
}
